import { DeskAppointmentService } from 'app/core/desk/desk-appointment.service';
// assistant-nlu.service.ts
import { Injectable } from '@angular/core';
import { filter, firstValueFrom, Subscription } from 'rxjs';
import { VoiceAssistantService } from './voice-assistant.service';
import moment from 'moment';

type Intent = 'CREATE_EVENT' | 'INFO_EVENT' | 'UNKNOWN';

interface CreateEventSlots {
    date?: Date;
    start?: string; // "HH:mm"
    end?: string;   // "HH:mm"
    title?: string;
}

type DialogState =
    | { mode: 'idle' }
    | { mode: 'create_event'; slots: CreateEventSlots };

@Injectable({ providedIn: 'root' })
export class AssistantNluService {
    private sub?: Subscription;
    private state: DialogState = { mode: 'idle' };

    constructor(private va: VoiceAssistantService, private deskAppointmentService: DeskAppointmentService) {

    }

    /** call once (e.g. AppComponent) */
    start(): void {
        if (this.sub) return;
        this.sub = this.va.utterance$()
            .pipe(filter(t => !!t?.trim()))
            .subscribe(t => void this.onUtterance(t.trim(), this.getLang()));
    }

    stop(): void {
        this.sub?.unsubscribe();
        this.sub = undefined;
    }

    // You already pass lang into VoiceAssistantService; if you store it elsewhere, use that.
    private getLang(): string {
        return 'de-DE';
    }

    private async onUtterance(text: string, lang: string): Promise<void> {
        // If we're in a dialog, treat input as continuation
        if (this.state.mode === 'create_event') {
            await this.continueCreateEvent(text, lang);
            return;
        }

        const intent = this.detectIntent(text, lang);

        if (intent === 'CREATE_EVENT') {
            const slots = this.extractCreateEventSlots(text, lang);

            this.state = { mode: 'create_event', slots };

            // ask missing slots
            await this.askNextMissingSlot(lang);
            return;
        }

        if (intent === 'INFO_EVENT') {
            const appointments = await firstValueFrom(this.deskAppointmentService.appointments$);
            if (appointments.length === 0) {
                await this.va.say('Du hast heute keine Termine.');
                return;
            }

            const now = new Date();
            const upcoming = appointments.filter(a => new Date(a.startDate) >= now);
            if (upcoming.length === 0) {
                await this.va.say('Du hast heute keine weiteren Termine.');
                return;
            }
            upcoming.sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());

            let response = `Du hast um ${moment(upcoming[0].startDate).format('HH:mm')} Uhr einen Termin mit dem Titel ${upcoming[0].title}.`;
            await this.va.say(response);
            return;
        }

        // fallback
        // (optional) you can say "Wie kann ich helfen?"
    }

    // ---------------- Intent detection (DE starter) ----------------
    private detectIntent(text: string, lang: string): Intent {
        const t = this.norm(text);

        if (lang.startsWith('de')) {
            // examples: "Erstelle einen Termin ...", "Trag einen Termin ein", "Neuer Termin ..."
            const hasCreate = /(erstelle|erstellen|mach|mache|lege|anlegen|trag|eintragen|setze|neuer)/.test(t);
            const hasEvent = /(termin|kalender|event|probe|gig|auftritt)/.test(t);
            if (hasCreate && hasEvent) return 'CREATE_EVENT';
            const info = /(Wann|habe|ich|mache|lege|anlegen|trag|eintragen|setze|neuer)\b/.test(t);
            if (info) return 'INFO_EVENT'
        }

        return 'UNKNOWN';
    }

    // ---------------- Create event dialog ----------------
    private async continueCreateEvent(text: string, lang: string): Promise<void> {
        if (this.state.mode !== 'create_event') return;

        const current = this.state.slots;
        const incoming = this.extractCreateEventSlots(text, lang);

        // merge (do not overwrite existing unless we got a new value)
        this.state.slots = {
            date: incoming.date ?? current.date,
            start: incoming.start ?? current.start,
            end: incoming.end ?? current.end,
            title: incoming.title ?? current.title,
        };

        await this.askNextMissingSlot(lang);
    }

    private async askNextMissingSlot(lang: string): Promise<void> {
        if (this.state.mode !== 'create_event') return;

        const s = this.state.slots;

        if (!s.date) {
            await this.va['activate'] ? Promise.resolve() : Promise.resolve(); // no-op; keeps TS happy if you lint private access
            // ask
            await this.va.say('Für welches Datum?'); // if you expose speak later, replace; see note below
            return;
        }

        if (!s.start || !s.end) {
            await this.va.say('Von wann bis wann?');
            return;
        }

        if (!s.title) {
            await this.va.say('Was soll ich notieren?');
            return;
        }

        // All slots filled -> confirm + persist
        const dateStr = this.formatDate(s.date!, lang);
        const confirm = `Ich habe für ${dateStr} von ${s.start} bis ${s.end} ${s.title} eingetragen.`;
        this.deskAppointmentService.saveAppointment({
            startDate: moment(s.date).set({
                hour: parseInt(s.start!.split(':')[0], 10),
                minute: parseInt(s.start!.split(':')[1], 10),
            }).toDate(),
            endDate: moment(s.date).set({
                hour: parseInt(s.end!.split(':')[0], 10),
                minute: parseInt(s.end!.split(':')[1], 10),
            }).toDate(),
            title: s.title,
            priority: 'MEDIUM',
        });

        await this.va.say(confirm);

        // reset dialog
        this.state = { mode: 'idle' };
    }

    // ---------------- Slot extraction ----------------
    private extractCreateEventSlots(text: string, lang: string): CreateEventSlots {
        const t = text.trim();
        const slots: CreateEventSlots = {};

        // date
        const date = this.parseDate(t, lang);
        if (date) slots.date = date;

        // time range
        const range = this.parseTimeRange(t, lang);
        if (range) {
            slots.start = range.start;
            slots.end = range.end;
        }

        // title / note
        const title = this.parseTitle(t, lang);
        if (title) slots.title = title;

        console.log('Extracted slots:', slots);

        return slots;
    }

    private parseTitle(text: string, lang: string): string | undefined {
        if (!lang.toLowerCase().startsWith('de')) return undefined;

        const raw = text.trim();
        if (!raw) return undefined;

        // 1) Strip command/date/time/filler – what remains is likely the note/title
        const candidate = this.stripKnownPartsDe(raw);

        // 2) Heuristics: avoid returning garbage / whole sentence
        // - must contain at least one letter
        // - must not be only very short
        // - must not still contain typical command words
        if (!candidate) return undefined;

        const lower = candidate.toLowerCase();
        if (/^(heute|morgen|übermorgen)$/.test(lower)) return undefined;
        if (/(erstelle|erstellen|eintragen|anlegen|termin|kalender|event)\b/.test(lower)) return undefined;
        if (!/[a-zäöüß]/i.test(candidate)) return undefined;
        if (candidate.length < 2) return undefined;

        return candidate;
    }

    private stripKnownPartsDe(input: string): string | '' {
        let t = input;

        // Command phrasing
        t = t.replace(
            /^(bitte\s*)?(erstelle|erstellen|mach|mache|lege|anlegen|trag|eintragen|setze|erstellt|neuer)\b\s*/i,
            ''
        );

        // "Termin/Event/Kalendereintrag"
        t = t.replace(/\b(einen|einem|ein|eine)\s+(termin|kalendereintrag|event)\b/ig, ' ');
        t = t.replace(/\b(termin|kalendereintrag|event)\b/ig, ' ');

        // Date anchors + payload
        t = t.replace(
            /\b(am|für)\s+(heute|morgen|übermorgen|montag|dienstag|mittwoch|donnerstag|freitag|samstag|sonntag|\d{1,2}\.\d{1,2}(?:\.\d{4})?)\b/ig,
            ' '
        );

        // ✅ Remove standalone relative days anywhere
        t = t.replace(/\b(heute|morgen|übermorgen)\b/ig, ' ');

        // Standalone weekdays
        t = t.replace(/\b(montag|dienstag|mittwoch|donnerstag|freitag|samstag|sonntag)\b/ig, ' ');

        // Absolute dates anywhere
        t = t.replace(/\b\d{1,2}\.\d{1,2}(?:\.\d{4})?\b/g, ' ');

        // Time ranges anywhere
        t = t.replace(
            /\b(?:von\s*)?\d{1,2}(?:[:.]\d{2})?\s*(?:uhr)?\s*(?:-|–|bis)\s*\d{1,2}(?:[:.]\d{2})?\s*(?:uhr)?\b/ig,
            ' '
        );

        // Single time "um 13:20"
        t = t.replace(/\b(um)\s*\d{1,2}(?:[:.]\d{2})?\s*(uhr)?\b/ig, ' ');

        // Filler words
        t = t.replace(/\b(am|für|um|von|bis|uhr|den|der|die|das|ein|eine|einen|einem|mir|bitte)\b/ig, ' ');

        // Cleanup
        t = t.replace(/\s+/g, ' ').trim();

        return t || '';
    }





    private parseTimeRange(text: string, lang: string): { start: string; end: string } | undefined {
        const t = this.norm(text);

        // DE: "14-16", "14:00-16:00", "14 bis 16", "von 14 bis 16"
        const m =
            t.match(/\b(?:von\s*)?(\d{1,2})(?:[:.](\d{2}))?\s*(?:uhr)?\s*(?:-|–|bis)\s*(\d{1,2})(?:[:.](\d{2}))?\s*(?:uhr)?\b/);

        if (!m) return undefined;

        const h1 = this.toInt(m[1]);
        const min1 = m[2] ? this.toInt(m[2]) : 0;
        const h2 = this.toInt(m[3]);
        const min2 = m[4] ? this.toInt(m[4]) : 0;

        if (!this.validHour(h1) || !this.validHour(h2) || !this.validMin(min1) || !this.validMin(min2)) return undefined;

        return { start: this.hhmm(h1, min1), end: this.hhmm(h2, min2) };
    }

    private parseDate(text: string, lang: string): Date | undefined {
        const t = this.norm(text);

        // absolute: dd.mm.yyyy or dd.mm (assume current/next year)
        const abs = t.match(/\b(\d{1,2})[.](\d{1,2})(?:[.](\d{4}))?\b/);
        if (abs) {
            const d = this.toInt(abs[1]);
            const m = this.toInt(abs[2]);
            const y = abs[3] ? this.toInt(abs[3]) : new Date().getFullYear();
            const dt = this.makeDateSafe(y, m, d);
            if (!dt) return undefined;

            // if no year given and date already passed -> next year
            if (!abs[3]) {
                const now = new Date();
                if (dt.getTime() < this.startOfDay(now).getTime()) {
                    const dt2 = this.makeDateSafe(y + 1, m, d);
                    if (dt2) return dt2;
                }
            }
            return dt;
        }

        // relative (DE starter)
        if (lang.startsWith('de')) {
            if (/\bheute\b/.test(t)) return this.startOfDay(new Date());
            if (/\bmorgen\b/.test(t)) return this.addDays(this.startOfDay(new Date()), 1);
            if (/\bübermorgen\b/.test(t)) return this.addDays(this.startOfDay(new Date()), 2);

            const wd = t.match(/\b(montag|dienstag|mittwoch|donnerstag|freitag|samstag|sonntag)\b/);
            if (wd) return this.nextWeekday(wd[1]);
        }

        return undefined;
    }

    private nextWeekday(deWord: string): Date {
        const map: Record<string, number> = {
            sonntag: 0,
            montag: 1,
            dienstag: 2,
            mittwoch: 3,
            donnerstag: 4,
            freitag: 5,
            samstag: 6,
        };
        const target = map[deWord] ?? 0;
        const today = this.startOfDay(new Date());
        const day = today.getDay();

        let delta = (target - day + 7) % 7;
        if (delta === 0) delta = 7; // "freitag" means next friday, not today
        return this.addDays(today, delta);
    }

    // ---------------- Formatting ----------------
    private formatDate(d: Date, lang: string): string {
        try {
            return new Intl.DateTimeFormat(lang, { weekday: 'long', year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
        } catch {
            // fallback
            return `${d.getDate().toString().padStart(2, '0')}.${(d.getMonth() + 1).toString().padStart(2, '0')}.${d.getFullYear()}`;
        }
    }

    // ---------------- Utils ----------------
    private norm(s: string): string {
        return s.toLowerCase().replace(/[^\p{L}\p{N}\s.:–-]/gu, ' ').replace(/\s+/g, ' ').trim();
    }
    private normKeepCase(s: string): string {
        return s.replace(/[^\p{L}\p{N}\s.:–-]/gu, ' ').replace(/\s+/g, ' ').trim();
    }
    private toInt(s: string): number {
        const n = Number.parseInt(s, 10);
        return Number.isFinite(n) ? n : NaN;
    }
    private validHour(h: number): boolean { return Number.isFinite(h) && h >= 0 && h <= 23; }
    private validMin(m: number): boolean { return Number.isFinite(m) && m >= 0 && m <= 59; }
    private hhmm(h: number, m: number): string {
        return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
    }
    private startOfDay(d: Date): Date {
        return new Date(d.getFullYear(), d.getMonth(), d.getDate());
    }
    private addDays(d: Date, days: number): Date {
        const x = new Date(d);
        x.setDate(x.getDate() + days);
        return x;
    }
    private makeDateSafe(y: number, m1: number, d: number): Date | undefined {
        if (m1 < 1 || m1 > 12) return undefined;
        const dt = new Date(y, m1 - 1, d);
        if (dt.getFullYear() !== y || dt.getMonth() !== (m1 - 1) || dt.getDate() !== d) return undefined;
        return this.startOfDay(dt);
    }
}
